import { ColumnDef } from '@tanstack/react-table';
import { Calendar, Eye, MapPin, MoreHorizontal, Package, Pencil, Plus, Ship, Trash2, Truck } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { ConfirmDialog, MessageDialog } from '../components/ui/alert-dialog-helper';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { createDateColumn } from '../components/ui/column-helpers';
import { DataTable } from '../components/ui/data-table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { Shipment, ShipmentStats, ShipmentType } from '../types/shipment';

type Role = { key: string; name: string; description: string };
type User = { id: string; email: string; first_name: string; last_name: string; roles: Role[] };

type ShipmentStatusFilter = 'all' | 'active' | 'delivered' | 'overdue';

const Shipments: React.FC = () => {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [allShipments, setAllShipments] = useState<Shipment[]>([]);
  const [stats, setStats] = useState<ShipmentStats | null>(null);
  const [statusFilter, setStatusFilter] = useState<ShipmentStatusFilter>('all');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shipmentToDelete, setShipmentToDelete] = useState<Shipment | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadShipments();
    loadStats();
    checkAuth();
  }, [sortBy, sortOrder, page, limit, debouncedSearch]);

  // Apply client-side filtering when statusFilter or allShipments change
  useEffect(() => {
    if (statusFilter === 'all') {
      setShipments(allShipments);
    } else {
      setShipments(allShipments.filter(s => {
        const status = getShipmentStatus(s).status.toLowerCase();
        if (statusFilter === 'active') return status === 'in transit';
        if (statusFilter === 'delivered') return status === 'delivered';
        if (statusFilter === 'overdue') return status === 'overdue';
        return true;
      }));
    }
  }, [statusFilter, allShipments]);

  const loadShipments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        sortBy,
        sortOrder,
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      const response = await fetch(`/api/shipments?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load shipments');
      }

      const data = await response.json();
      const shipmentsData = data.success ? data.data : [];
      setAllShipments(shipmentsData);
      setShipments(shipmentsData);
      if (data.pagination) {
        setTotal(data.pagination.total || 0);
        setPages(data.pagination.pages || 1);
      }
    } catch (err) {
      console.error('Error loading shipments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = (filter: ShipmentStatusFilter) => {
    setStatusFilter(filter);
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/shipments/stats', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load shipment stats');
      }

      const data = await response.json();
      setStats(data.success ? data.data : null);
    } catch (err) {
      console.error('Error loading shipment stats:', err);
    }
  };

  const checkAuth = () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      }
    } catch (err) {
      console.error('Error reading user data:', err);
    }
  };

  const viewDetails = (shipment: Shipment) => {
    navigate(`/shipments/${shipment.id}`);
  };

  const canManageShipments = user?.roles?.some(role => ['admin', 'supervisor'].includes(role.key)) ?? false;

  const handleDeleteShipment = async () => {
    if (!shipmentToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/shipments/${shipmentToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success) {
        loadShipments();
      } else {
        setErrorMessage(data.message || 'Failed to delete shipment');
        setErrorDialogOpen(true);
      }
    } catch (err) {
      console.error('Error deleting shipment:', err);
      setErrorMessage('An error occurred while deleting the shipment');
      setErrorDialogOpen(true);
    } finally {
      setShipmentToDelete(null);
    }
  };

  const getShipmentTypeIcon = (type: ShipmentType) => {
    switch (type) {
      case ShipmentType.INBOUND:
        return <Ship className="h-4 w-4" />;
      case ShipmentType.OUTBOUND:
        return <Truck className="h-4 w-4" />;
      case ShipmentType.TRANSFER:
        return <Package className="h-4 w-4" />;
      default:
        return <Package className="h-4 w-4" />;
    }
  };

  const getShipmentTypeBadgeVariant = (type: ShipmentType) => {
    switch (type) {
      case ShipmentType.INBOUND:
        return 'default' as const;
      case ShipmentType.OUTBOUND:
        return 'secondary' as const;
      case ShipmentType.TRANSFER:
        return 'warning' as const;
      default:
        return 'secondary' as const;
    }
  };

  const getShipmentStatus = (shipment: Shipment) => {
    const jobs = shipment.jobs || [];
    
    // No jobs linked - use legacy timestamp-based logic
    if (jobs.length === 0) {
      if (shipment.actual_arrival_at) {
        return { status: 'Delivered', variant: 'default' as const };
      }
      return { status: 'Pending', variant: 'secondary' as const };
    }

    // Derive status from job statuses
    const statuses = jobs.map(j => j.status);
    
    if (statuses.every(s => s === 'completed')) {
      return { status: 'Delivered', variant: 'default' as const };
    }
    if (statuses.every(s => s === 'cancelled')) {
      return { status: 'Cancelled', variant: 'secondary' as const };
    }
    if (statuses.some(s => s === 'in_progress')) {
      return { status: 'In Transit', variant: 'warning' as const };
    }
    if (statuses.some(s => s === 'assigned')) {
      return { status: 'Assigned', variant: 'secondary' as const };
    }
    return { status: 'Pending', variant: 'secondary' as const };
  };



  const openCreatePage = () => {
    navigate('/shipments/new');
  };

  const handleSortingChange = (newSortBy: string, newSortOrder: 'ASC' | 'DESC') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPage(1);
  };

  const columns: ColumnDef<Shipment>[] = [
    {
      accessorKey: 'bl_number',
      header: 'BL Number',
      enableSorting: true,
      cell: ({ row }) => {
        const shipment = row.original;
        return (
          <button
            onClick={() => viewDetails(shipment)}
            className="font-medium text-gray-900 hover:text-gray-700 underline hover:no-underline"
          >
            {shipment.bl_number}
          </button>
        );
      },
    },
    {
      accessorKey: 'shipment_type',
      header: 'Type',
      enableSorting: true,
      cell: ({ row }) => {
        const shipment = row.original;
        return (
          <div className="flex items-center">
            {getShipmentTypeIcon(shipment.shipment_type)}
            <Badge variant={getShipmentTypeBadgeVariant(shipment.shipment_type)} className="ml-2">
              {shipment.shipment_type.charAt(0).toUpperCase() + shipment.shipment_type.slice(1)}
            </Badge>
          </div>
        );
      },
    },
    {
      accessorKey: 'origin_address',
      header: 'Origin',
      cell: ({ row }) => {
        const shipment = row.original;
        return shipment.origin_address ? (
          <div className="flex items-center text-sm">
            <MapPin className="h-3 w-3 mr-1 text-gray-400" />
            <span>{shipment.origin_address.city}, {shipment.origin_address.region}</span>
          </div>
        ) : (
          <span className="text-gray-400 italic">No origin</span>
        );
      },
    },
    {
      accessorKey: 'destination_address',
      header: 'Destination',
      cell: ({ row }) => {
        const shipment = row.original;
        return shipment.destination_address ? (
          <div className="flex items-center text-sm">
            <MapPin className="h-3 w-3 mr-1 text-gray-400" />
            <span>{shipment.destination_address.city}, {shipment.destination_address.region}</span>
          </div>
        ) : (
          <span className="text-gray-400 italic">No destination</span>
        );
      },
    },
    {
      accessorKey: 'asset_instances',
      header: 'Items',
      cell: ({ row }) => {
        const shipment = row.original;
        return (
          <div className="text-center">
            <span className="text-lg font-bold text-gray-900">
              {shipment.asset_instances?.length || 0}
            </span>
            <p className="text-xs text-gray-500">items</p>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const shipment = row.original;
        const { status, variant } = getShipmentStatus(shipment);
        return <Badge variant={variant}>{status}</Badge>;
      },
    },
    {
      accessorKey: 'planned_arrival_at',
      header: 'Planned Arrival',
      enableSorting: true,
      cell: ({ row }) => {
        const shipment = row.original;
        return shipment.planned_arrival_at ? (
          <div className="flex items-center text-sm">
            <Calendar className="h-3 w-3 mr-1 text-gray-400" />
            <span>{new Date(shipment.planned_arrival_at).toLocaleDateString()}</span>
          </div>
        ) : (
          <span className="text-gray-400 italic">Not set</span>
        );
      },
    },
    createDateColumn<Shipment>('created_at', 'Created At', 'datetime'),
    {
      accessorKey: 'created_by_user',
      header: 'Created By',
      cell: ({ row }) => {
        const shipment = row.original;
        return shipment.created_by_user
          ? `${shipment.created_by_user.first_name} ${shipment.created_by_user.last_name}`
          : 'System';
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      enableHiding: false,
      cell: ({ row }) => {
        const shipment = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => viewDetails(shipment)}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </DropdownMenuItem>
              {canManageShipments && (
                <>
                  <DropdownMenuItem onClick={() => navigate(`/shipments/${shipment.id}/edit`)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-red-600"
                    onClick={() => {
                      setShipmentToDelete(shipment);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h2 className="text-xl font-heading text-primary">Loading Shipments...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="shipments" />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-heading font-bold text-primary leading-tight mb-2">
            Shipments & Logistics
          </h2>
        </div>

        {/* Space before table toolbar */}
        <div className="mb-2" />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div
            onClick={() => handleFilterClick('all')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              statusFilter === 'all' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-primary">{stats?.total || 0}</div>
            <div className="text-sm text-muted-foreground">Total Shipments</div>
          </div>
          <div
            onClick={() => handleFilterClick('active')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              statusFilter === 'active' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-blue-600">{stats?.active || 0}</div>
            <div className="text-sm text-muted-foreground">Active</div>
          </div>
          <div
            onClick={() => handleFilterClick('delivered')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              statusFilter === 'delivered' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-green-600">{stats?.delivered || 0}</div>
            <div className="text-sm text-muted-foreground">Delivered</div>
          </div>
          <div
            onClick={() => handleFilterClick('overdue')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              statusFilter === 'overdue' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-red-600">{stats?.overdue || 0}</div>
            <div className="text-sm text-muted-foreground">Overdue</div>
          </div>
        </div>

        {/* Shipments Table */}
        <DataTable
          columns={columns}
          data={shipments}
          loading={loading}
          searchKey="bl_number"
          searchPlaceholder="Search by BL number..."
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          serverSide={true}
          pagination={{ total, page, limit, pages }}
          onPaginationChange={(newPage, newLimit) => {
            if (newLimit !== limit) { setLimit(newLimit); setPage(1); } else { setPage(newPage); }
          }}
          onSortingChange={handleSortingChange}
          toolbarContentRight={canManageShipments ? (
            <Button onClick={openCreatePage}>
              <Plus className="h-4 w-4 mr-2" />
              Create Shipment
            </Button>
          ) : undefined}
          emptyMessage="No shipments found. Create your first shipment to get started."
        />
      </main>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Shipment"
        description={`Are you sure you want to delete shipment ${shipmentToDelete?.bl_number}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteShipment}
        variant="destructive"
      />

      {/* Error Message Dialog */}
      <MessageDialog
        open={errorDialogOpen}
        onOpenChange={setErrorDialogOpen}
        title="Error"
        description={errorMessage}
        variant="destructive"
      />
    </div>
  );
};

export default Shipments;
